// ============================================================
//  src/pages/ProfilePage.js
//
//  Your account: who you are, your plan, your bots, and how your
//  account is secured. Styles live in ProfilePage.css (prf- prefix).
//
//  Backend: GET/POST /account/ (profile, change password, sign out
//  other devices), /billing/ (plan), /bots/ (bot list), and the
//  /auth/forgot/ email-code flow for people who forgot their password.
// ============================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  listBots, getBillingOverview, getAccount, updateAccountProfile, changeAccountPassword, signOutOtherDevices,
  forgotPasswordReq, forgotPasswordVerify, forgotPasswordReset,
} from '../api/client';
import './ProfilePage.css';

// ── helpers ───────────────────────────────────────────────────
const errMsg = (e, fallback = 'Something went wrong') => e?.response?.data?.message ?? fallback;

function parseDate(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}
function fmtDate(v, opts = { day: 'numeric', month: 'short', year: 'numeric' }) {
  const d = parseDate(v);
  return d ? d.toLocaleDateString('en-GB', opts) : '—';
}
function ago(v) {
  const d = parseDate(v);
  if (!d) return '—';
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)} days ago`;
  return fmtDate(v);
}
function memberFor(v) {
  const d = parseDate(v);
  if (!d) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return 'Joined today';
  if (days < 31) return `Member for ${days} day${days === 1 ? '' : 's'}`;
  const months = Math.floor(days / 30.4);
  if (months < 12) return `Member for ${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(months / 12);
  return `Member for ${years} year${years === 1 ? '' : 's'}`;
}
const fmtNum = n => (Number(n) || 0).toLocaleString();
const COLORS = ['#0E9F6E', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#4F46E5', '#059669'];
function colorFor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return COLORS[Math.abs(h) % COLORS.length];
}
function initials(name = '') {
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?';
}
function strength(pw) {
  const checks = [pw.length >= 8, /[A-Z]/.test(pw) && /[a-z]/.test(pw), /\d/.test(pw), /[^A-Za-z0-9]/.test(pw), pw.length >= 12];
  const score = checks.filter(Boolean).length;
  if (!pw) return { score: 0, label: '', tone: '' };
  if (score <= 2) return { score: 1, label: 'Weak', tone: 'weak' };
  if (score === 3) return { score: 2, label: 'Okay', tone: 'ok' };
  if (score === 4) return { score: 3, label: 'Strong', tone: 'good' };
  return { score: 4, label: 'Very strong', tone: 'great' };
}

// ── small pieces ──────────────────────────────────────────────
function Section({ id, icon, title, sub, action, children, tone }) {
  return (
    <section id={id} className={`prf-card ${tone ? `is-${tone}` : ''}`}>
      <header className="prf-card-head">
        <span className="prf-card-ic"><i className={`fa-solid ${icon}`} /></span>
        <div className="prf-card-title"><h2>{title}</h2>{sub && <p>{sub}</p>}</div>
        {action}
      </header>
      {children}
    </section>
  );
}

function Field({ label, hint, error, children, right }) {
  return (
    <label className={`prf-field ${error ? 'is-err' : ''}`}>
      <span className="prf-field-label">{label}{right}</span>
      {children}
      {error ? <span className="prf-field-err">{error}</span> : hint ? <span className="prf-field-hint">{hint}</span> : null}
    </label>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete, autoFocus, invalid }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="prf-input-wrap">
      <input className={`prf-input ${invalid ? 'is-invalid' : ''}`} type={shown ? 'text' : 'password'} value={value} placeholder={placeholder}
        autoComplete={autoComplete} autoFocus={autoFocus} spellCheck={false} onChange={e => onChange(e.target.value)} />
      <button type="button" className="prf-eye" onClick={() => setShown(s => !s)} aria-label={shown ? 'Hide password' : 'Show password'}>
        <i className={`fa-regular ${shown ? 'fa-eye-slash' : 'fa-eye'}`} />
      </button>
    </div>
  );
}

function StrengthMeter({ password }) {
  const s = strength(password);
  if (!password) return null;
  return (
    <div className={`prf-strength is-${s.tone}`}>
      <div className="prf-strength-bars">{[1, 2, 3, 4].map(i => <span key={i} className={i <= s.score ? 'on' : ''} />)}</div>
      <span>{s.label}</span>
    </div>
  );
}

function Ring({ pct, children, tone = 'brand' }) {
  const r = 26, c = 2 * Math.PI * r;
  return (
    <div className={`prf-ring is-${tone}`}>
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r={r} className="prf-ring-track" />
        <circle cx="32" cy="32" r={r} className="prf-ring-fill" strokeDasharray={`${Math.max(0, Math.min(1, pct)) * c} ${c}`} />
      </svg>
      <div className="prf-ring-label">{children}</div>
    </div>
  );
}

// ── Personal details ──────────────────────────────────────────
function PersonalForm({ profile, onSaved }) {
  const [form, setForm] = useState({ fname: profile.fname, lname: profile.lname, phone: profile.phone });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ fname: profile.fname, lname: profile.lname, phone: profile.phone }); }, [profile.fname, profile.lname, profile.phone]);

  const dirty = form.fname !== profile.fname || form.lname !== profile.lname || form.phone !== profile.phone;
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  async function save(e) {
    e.preventDefault();
    const next = {};
    if (!form.fname.trim()) next.fname = 'Enter your first name';
    if (!form.lname.trim()) next.lname = 'Enter your last name';
    if (!/^\+?[0-9]{7,15}$/.test(form.phone.replace(/[\s\-()]/g, ''))) next.phone = 'Enter a valid phone number';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      const res = await updateAccountProfile({ fname: form.fname.trim(), lname: form.lname.trim(), phone: form.phone });
      if (res.data?.success) { toast.success('Profile saved'); onSaved(res.data.profile); }
    } catch (err) {
      const field = err?.response?.data?.field;
      if (field) setErrors(x => ({ ...x, [field]: errMsg(err) })); else toast.error(errMsg(err));
    }
    setSaving(false);
  }

  return (
    <form className="prf-form" onSubmit={save} noValidate>
      <div className="prf-grid-2">
        <Field label="First name" error={errors.fname}>
          <input className="prf-input" value={form.fname} onChange={e => set('fname', e.target.value)} autoComplete="given-name" maxLength={50} />
        </Field>
        <Field label="Last name" error={errors.lname}>
          <input className="prf-input" value={form.lname} onChange={e => set('lname', e.target.value)} autoComplete="family-name" maxLength={50} />
        </Field>
      </div>
      <Field label="Phone number" error={errors.phone} hint="Used by support to reach you about your account.">
        <input className="prf-input" value={form.phone} onChange={e => set('phone', e.target.value)} inputMode="tel" autoComplete="tel" placeholder="0803 123 4567" />
      </Field>
      <Field label="Email" hint="This is how you sign in and where reset codes go. To change it, contact support."
        right={profile.verified ? <em className="prf-badge is-ok"><i className="fa-solid fa-circle-check" /> Verified</em> : <em className="prf-badge is-warn">Not verified</em>}>
        <div className="prf-input is-readonly"><i className="fa-regular fa-envelope" /> {profile.email}</div>
      </Field>
      <div className={`prf-form-foot prf-save-foot ${dirty ? 'is-dirty' : ''}`}>
        {dirty && <span className="prf-unsaved"><i className="fa-solid fa-circle" /> Unsaved changes</span>}
        {dirty && <button type="button" className="prf-btn ghost" onClick={() => { setForm({ fname: profile.fname, lname: profile.lname, phone: profile.phone }); setErrors({}); }} disabled={saving}>Discard</button>}
        <button type="submit" className="prf-btn primary" disabled={!dirty || saving}>
          {saving ? <span className="prf-spin" /> : <i className="fa-solid fa-check" />} Save changes
        </button>
      </div>
    </form>
  );
}

// ── Security ──────────────────────────────────────────────────
function ChangePassword({ onTokenRotated }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const s = strength(next);

  async function submit(e) {
    e.preventDefault();
    const er = {};
    if (!current) er.current = 'Enter your current password';
    if (next.length < 8) er.password = 'Use at least 8 characters';
    else if (next === current) er.password = 'Choose a password you haven’t used here';
    if (confirm !== next) er.confirm = 'Passwords don’t match';
    setErrors(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    try {
      const res = await changeAccountPassword(current, next);
      if (res.data?.success) {
        onTokenRotated(res.data.token);
        toast.success('Password changed — other devices were signed out');
        setCurrent(''); setNext(''); setConfirm(''); setErrors({});
      }
    } catch (err) {
      const field = err?.response?.data?.field;
      if (field) setErrors(x => ({ ...x, [field]: errMsg(err) })); else toast.error(errMsg(err));
    }
    setBusy(false);
  }

  return (
    <form className="prf-form" onSubmit={submit} noValidate>
      <Field label="Current password" error={errors.current}>
        <PasswordInput value={current} onChange={v => { setCurrent(v); setErrors(x => ({ ...x, current: '' })); }} autoComplete="current-password" invalid={!!errors.current} />
      </Field>
      <div className="prf-grid-2">
        <Field label="New password" error={errors.password}>
          <PasswordInput value={next} onChange={v => { setNext(v); setErrors(x => ({ ...x, password: '' })); }} autoComplete="new-password" placeholder="At least 8 characters" invalid={!!errors.password} />
          <StrengthMeter password={next} />
        </Field>
        <Field label="Confirm new password" error={errors.confirm}>
          <PasswordInput value={confirm} onChange={v => { setConfirm(v); setErrors(x => ({ ...x, confirm: '' })); }} autoComplete="new-password" invalid={!!errors.confirm} />
          {confirm && confirm === next && <span className="prf-match"><i className="fa-solid fa-check" /> Matches</span>}
        </Field>
      </div>
      {next && s.score < 3 && <p className="prf-tip"><i className="fa-solid fa-lightbulb" /> Longer is stronger — try three random words with a number, like <code>mango-river-42</code>.</p>}
      <div className="prf-form-foot">
        <button type="submit" className="prf-btn primary" disabled={busy || !current || !next || !confirm}>
          {busy ? <span className="prf-spin" /> : <i className="fa-solid fa-key" />} Change password
        </button>
      </div>
    </form>
  );
}

function ResetByEmail({ email, onDone }) {
  const [step, setStep] = useState('start');   // start | code | done
  const [otp, setOtp] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft(x => x - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);

  async function send() {
    setBusy(true);
    try {
      await forgotPasswordReq({ email });
      toast.success('Code sent — check your email');
      setStep('code'); setLeft(60);
    } catch (e) { toast.error(errMsg(e, 'Could not send the code')); }
    setBusy(false);
  }

  async function reset(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { toast.error('Enter the 6-digit code from your email'); return; }
    if (pw.length < 8) { toast.error('Use at least 8 characters'); return; }
    setBusy(true);
    try {
      const v = await forgotPasswordVerify({ email, otp });
      const resetToken = v.data?.data?.reset_token;
      if (!resetToken) throw new Error('Invalid code');
      await forgotPasswordReset({ email, reset_token: resetToken, password: pw });
      setStep('done');
      onDone();
    } catch (err) { toast.error(errMsg(err, 'That code didn’t work — request a new one')); }
    setBusy(false);
  }

  if (step === 'start') {
    return (
      <div className="prf-reset">
        <p>Forgot your current password? We’ll email a 6-digit code to <b>{email}</b> instead.</p>
        <button type="button" className="prf-btn ghost" onClick={send} disabled={busy}>
          {busy ? <span className="prf-spin dark" /> : <i className="fa-regular fa-envelope" />} Email me a code
        </button>
      </div>
    );
  }
  if (step === 'done') return null;
  return (
    <form className="prf-form prf-reset is-open" onSubmit={reset} noValidate>
      <p>Enter the code we sent to <b>{email}</b>. You’ll be signed out everywhere and asked to sign in with the new password.</p>
      <div className="prf-grid-2">
        <Field label="6-digit code" right={left > 0 ? <em className="prf-muted">Resend in {left}s</em> : <button type="button" className="prf-link" onClick={send} disabled={busy}>Resend</button>}>
          <input className="prf-input prf-code" value={otp} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
            onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
        </Field>
        <Field label="New password">
          <PasswordInput value={pw} onChange={setPw} autoComplete="new-password" placeholder="At least 8 characters" />
          <StrengthMeter password={pw} />
        </Field>
      </div>
      <div className="prf-form-foot">
        <button type="button" className="prf-btn ghost" onClick={() => { setStep('start'); setOtp(''); setPw(''); }} disabled={busy}>Cancel</button>
        <button type="submit" className="prf-btn primary" disabled={busy}>{busy ? <span className="prf-spin" /> : <i className="fa-solid fa-rotate" />} Reset password</button>
      </div>
    </form>
  );
}

// ── Page ──────────────────────────────────────────────────────
export default function ProfilePage() {
  const navigate = useNavigate();
  const { user: ctxUser, refreshUser, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileErr, setProfileErr] = useState('');
  const [bots, setBots] = useState(null);
  const [plan, setPlan] = useState(undefined);       // undefined = loading, null = none
  const [access, setAccess] = useState(null);         // trial / active / expired + rental cover
  const [signingOut, setSigningOut] = useState(false);

  const load = useCallback(() => {
    getAccount()
      .then(res => { if (res.data?.success) setProfile(res.data.profile); else setProfileErr('Could not load your profile'); })
      .catch(e => setProfileErr(errMsg(e, 'Could not load your profile')));
    listBots().then(res => setBots(res.data?.bots ?? [])).catch(() => setBots([]));
    getBillingOverview().then(res => { setPlan(res.data?.current_plan ?? null); setAccess(res.data?.access ?? null); }).catch(() => setPlan(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fall back to the signed-in user while /account/ loads, so the header never flashes empty.
  const p = profile ?? {
    fname: ctxUser?.fname ?? ctxUser?.sFname ?? '', lname: ctxUser?.lname ?? ctxUser?.sLname ?? '',
    email: ctxUser?.email ?? ctxUser?.sEmail ?? '', phone: ctxUser?.phone ?? ctxUser?.sPhone ?? '',
    verified: true, createdAt: ctxUser?.created_at ?? null, id: ctxUser?.id ?? ctxUser?.sId ?? null,
  };
  const fullName = `${p.fname} ${p.lname}`.trim() || p.email || 'Your account';

  const stats = useMemo(() => {
    const list = bots ?? [];
    const active = list.filter(b => Number(b.bIsActive) === 1).length;
    const customers = list.reduce((a, b) => a + (Number(b.totalUsers) || 0), 0);
    const commands = list.reduce((a, b) => a + (Number(b.totalCommands) || 0), 0);
    return { total: list.length, active, customers, commands };
  }, [bots]);

  const planName = plan?.plName || 'Free trial';
  const isTrial = !!plan?.isTrial;
  const paused = access?.state === 'expired';
  const rentalBots = Number(access?.rentalBots) || 0;
  const expires = parseDate(plan?.subExpiresAt);
  const intervalDays = Number(plan?.plInterval) || 30;
  const daysLeft = expires ? Math.max(0, Math.ceil((expires.getTime() - Date.now()) / 86400000)) : null;
  const maxBots = Number(plan?.plMaxBots) || 0;
  const planTone = paused ? 'danger' : daysLeft === null ? 'brand' : daysLeft <= 3 ? 'danger' : daysLeft <= 7 ? 'warn' : 'brand';

  function onSaved(next) {
    setProfile(next);
    refreshUser?.({ fname: next.fname, lname: next.lname, phone: next.phone, sFname: next.fname, sLname: next.lname, sPhone: next.phone });
  }
  function storeToken(t) {
    if (t) { try { localStorage.setItem('sAuthToken', t); } catch { /* storage blocked */ } }
  }
  async function signOutOthers() {
    setSigningOut(true);
    try {
      const res = await signOutOtherDevices();
      if (res.data?.success) { storeToken(res.data.token); toast.success('Signed out of every other device'); }
    } catch (e) { toast.error(errMsg(e)); }
    setSigningOut(false);
  }
  async function afterEmailReset() {
    toast.success('Password reset — sign in with your new password');
    await logout?.();
    navigate('/login', { replace: true });
  }
  function copy(text, what) {
    navigator.clipboard?.writeText(String(text)).then(() => toast.success(`${what} copied`)).catch(() => {});
  }

  // Account checklist — things that make an account safer / more useful
  const checks = [
    { done: !!p.verified, label: 'Email verified', icon: 'fa-envelope-circle-check' },
    { done: !!p.phone, label: 'Phone number added', icon: 'fa-phone', to: '#prf-personal' },
    { done: stats.total > 0, label: 'First bot created', icon: 'fa-robot', to: '/dashboard' },
    { done: stats.active > 0, label: 'A bot is live', icon: 'fa-signal', to: '/dashboard' },
    { done: plan !== undefined && !!plan && (Number(plan.plPrice) || 0) > 0, label: 'On a paid plan', icon: 'fa-crown', to: '/billing' },
  ];
  const doneCount = checks.filter(c => c.done).length;

  return (
    <div className="prf">
      {/* ── Hero ── */}
      <header className="prf-hero">
        <div className="prf-hero-glow" aria-hidden="true" />
        <div className="prf-hero-main">
          <div className="prf-avatar" style={{ background: colorFor(fullName) }} aria-hidden="true">
            {initials(fullName)}
            {p.verified && <span className="prf-avatar-tick" title="Verified"><i className="fa-solid fa-check" /></span>}
          </div>
          <div className="prf-hero-text">
            <span className="prf-eyebrow">Your account</span>
            <h1>{fullName}</h1>
            <div className="prf-hero-meta">
              <span><i className="fa-regular fa-envelope" /> {p.email}</span>
              {p.createdAt && <span><i className="fa-regular fa-calendar" /> {memberFor(p.createdAt)}</span>}
              {p.id && (
                <button type="button" className="prf-chip" onClick={() => copy(p.id, 'Account ID')} title="Copy account ID">
                  ID #{p.id} <i className="fa-regular fa-copy" />
                </button>
              )}
            </div>
          </div>
          <div className="prf-hero-plan">
            <span className="prf-plan-pill"><i className="fa-solid fa-crown" /> {planName}</span>
            <Link to="/billing" className="prf-btn on-dark">{(Number(plan?.plPrice) || 0) > 0 ? 'Manage plan' : 'Upgrade'} <i className="fa-solid fa-arrow-right" /></Link>
          </div>
        </div>

        <div className="prf-stats">
          <div className="prf-stat">
            <span className="prf-stat-ic"><i className="fa-solid fa-robot" /></span>
            <b>{bots === null ? '…' : fmtNum(stats.total)}</b>
            <span>{bots === null ? 'Bots' : `Bot${stats.total === 1 ? '' : 's'} · ${stats.active} live`}</span>
          </div>
          <div className="prf-stat">
            <span className="prf-stat-ic"><i className="fa-solid fa-users" /></span>
            <b>{bots === null ? '…' : fmtNum(stats.customers)}</b>
            <span>Customers reached</span>
          </div>
          <div className="prf-stat">
            <span className="prf-stat-ic"><i className="fa-solid fa-terminal" /></span>
            <b>{bots === null ? '…' : fmtNum(stats.commands)}</b>
            <span>Commands built</span>
          </div>
          <div className="prf-stat">
            <span className="prf-stat-ic"><i className="fa-solid fa-hourglass-half" /></span>
            <b>{plan === undefined ? '…' : daysLeft === null ? '∞' : daysLeft}</b>
            <span>{daysLeft === null ? 'No expiry' : paused ? `${planName} ended — bots paused` : `Day${daysLeft === 1 ? '' : 's'} left on ${planName}`}</span>
          </div>
        </div>
      </header>

      {profileErr && <div className="prf-alert"><i className="fa-solid fa-circle-exclamation" /> {profileErr} <button type="button" className="prf-link" onClick={load}>Try again</button></div>}

      <div className="prf-layout">
        {/* ── main column ── */}
        <div className="prf-col">
          <Section id="prf-personal" icon="fa-user" title="Personal details" sub="How we address you and reach you.">
            {profile ? <PersonalForm profile={profile} onSaved={onSaved} /> : <div className="prf-skel"><span /><span /><span /></div>}
          </Section>

          <Section id="prf-security" icon="fa-shield-halved" title="Password & security" sub="Keep your account and your bots safe.">
            <div className="prf-sub-head"><b>Change password</b><span>You’ll stay signed in here. Every other device is signed out and you get a confirmation email.</span></div>
            <ChangePassword onTokenRotated={storeToken} />
            <div className="prf-divider" />
            <ResetByEmail email={p.email} onDone={afterEmailReset} />
            <div className="prf-divider" />
            <div className="prf-row">
              <span className="prf-row-ic"><i className="fa-solid fa-mobile-screen-button" /></span>
              <div className="prf-row-text">
                <b>Sign out other devices</b>
                <span>Your account allows one active sign-in. Use this if you signed in on a shared or lost device — only this one stays signed in.{p.lastActivity ? ` Last activity ${ago(p.lastActivity)}.` : ''}</span>
              </div>
              <button type="button" className="prf-btn ghost" onClick={signOutOthers} disabled={signingOut}>
                {signingOut ? <span className="prf-spin dark" /> : <i className="fa-solid fa-right-from-bracket" />} Sign out others
              </button>
            </div>
          </Section>
        </div>

        {/* ── side column ── */}
        <aside className="prf-col">
          <Section icon="fa-crown" title="Your plan" tone="plan"
            action={<Link to="/billing" className="prf-link">Billing <i className="fa-solid fa-arrow-right" /></Link>}>
            {plan === undefined ? <div className="prf-skel"><span /><span /></div> : (
              <>
                <div className="prf-plan">
                  <Ring pct={daysLeft === null ? 1 : daysLeft / intervalDays} tone={planTone}>
                    <b>{daysLeft === null ? '∞' : daysLeft}</b><span>{daysLeft === null ? 'no expiry' : 'days'}</span>
                  </Ring>
                  <div className="prf-plan-text">
                    <b>{planName}</b>
                    <span>{(Number(plan?.plPrice) || 0) > 0 ? `₦${fmtNum(plan.plPrice)} / ${intervalDays} days` : `${intervalDays}-day trial · once per account`}</span>
                    <span className={paused ? 'prf-plan-ended' : ''}>{expires ? (paused ? `Ended ${fmtDate(plan.subExpiresAt)} — bots paused` : `${isTrial ? 'Ends' : 'Renews or ends'} ${fmtDate(plan.subExpiresAt)}`) : 'No end date'}</span>
                  </div>
                </div>
                {maxBots > 0 && (
                  <div className="prf-meter">
                    <div className="prf-meter-top"><span>Bot slots</span><b>{stats.total} / {maxBots}</b></div>
                    <div className="prf-meter-bar"><span style={{ width: `${Math.min(100, (stats.total / maxBots) * 100)}%` }} className={stats.total >= maxBots ? 'is-full' : ''} /></div>
                    {stats.total >= maxBots && <span className="prf-meter-note">All slots used — upgrade to add more bots.</span>}
                  </div>
                )}
                {(paused || (daysLeft !== null && daysLeft <= 7)) && (
                  <Link to="/billing" className={`prf-plan-cta ${paused || daysLeft <= 3 ? 'is-danger' : ''}`}>
                    <i className="fa-solid fa-bolt" /> {paused
                      ? (isTrial ? 'Your free trial has ended — your bots are paused. Choose a plan' : 'Your plan has ended — your bots are paused. Renew now')
                      : `${isTrial ? 'Trial ends' : 'Ends'} in ${daysLeft} day${daysLeft === 1 ? '' : 's'} — ${isTrial ? 'pick a plan' : 'renew'} to keep your bots running`}
                  </Link>
                )}
                {rentalBots > 0 && (
                  <div className="prf-rental-note">
                    <i className="fa-solid fa-sim-card" />
                    <span><b>{rentalBots} bot{rentalBots === 1 ? '' : 's'} on rented numbers</b> {rentalBots === 1 ? 'is' : 'are'} covered by Rent Space — {rentalBots === 1 ? 'it keeps' : 'they keep'} running while the rent is paid, whatever your plan says.</span>
                  </div>
                )}
              </>
            )}
          </Section>

          <Section icon="fa-list-check" title="Account checklist" sub={`${doneCount} of ${checks.length} done`}>
            <div className="prf-progress"><span style={{ width: `${(doneCount / checks.length) * 100}%` }} /></div>
            <ul className="prf-checks">
              {checks.map(c => {
                const inner = (<><span className={`prf-check-ic ${c.done ? 'is-done' : ''}`}><i className={`fa-solid ${c.done ? 'fa-check' : c.icon}`} /></span><span>{c.label}</span>{!c.done && c.to && <i className="fa-solid fa-chevron-right prf-check-go" />}</>);
                if (c.done || !c.to) return <li key={c.label} className={c.done ? 'is-done' : ''}>{inner}</li>;
                return <li key={c.label}>{c.to.startsWith('#')
                  ? <a href={c.to} onClick={e => { e.preventDefault(); document.querySelector(c.to)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>{inner}</a>
                  : <Link to={c.to}>{inner}</Link>}</li>;
              })}
            </ul>
          </Section>

          <Section icon="fa-robot" title="Your bots" action={<Link to="/dashboard" className="prf-link">All <i className="fa-solid fa-arrow-right" /></Link>}>
            {bots === null ? <div className="prf-skel"><span /><span /></div> : bots.length === 0 ? (
              <div className="prf-empty"><i className="fa-solid fa-robot" /><span>No bots yet.</span><Link to="/dashboard" className="prf-btn primary sm"><i className="fa-solid fa-plus" /> Create a bot</Link></div>
            ) : (
              <ul className="prf-bots">
                {bots.slice(0, 5).map(b => (
                  <li key={b.bId}>
                    <Link to={`/bots/${b.bId}`}>
                      <span className="prf-bot-av" style={{ background: colorFor(b.bName || b.bId) }}>{initials(b.bName || '?')}</span>
                      <span className="prf-bot-text"><b>{b.bName}</b><span>{fmtNum(b.totalUsers)} customers · {fmtNum(b.totalCommands)} commands</span></span>
                      <span className={`prf-dot ${Number(b.bIsActive) === 1 ? 'is-live' : ''}`} title={Number(b.bIsActive) === 1 ? 'Live' : 'Paused'} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section icon="fa-code" title="Developer">
            <div className="prf-dev">
              <button type="button" className="prf-dev-row" onClick={() => p.id && copy(p.id, 'Account ID')}>
                <span>Account ID</span><code>#{p.id ?? '—'}</code><i className="fa-regular fa-copy" />
              </button>
              <Link to="/settings/webhook" className="prf-dev-row">
                <span>Webhook &amp; API token</span><code>Manage</code><i className="fa-solid fa-chevron-right" />
              </Link>
              <Link to="/how-to-use" className="prf-dev-row">
                <span>Developer guide</span><code>Docs</code><i className="fa-solid fa-chevron-right" />
              </Link>
            </div>
          </Section>
        </aside>
      </div>
    </div>
  );
}